import React, { useState, useEffect } from "react";
import {
  Dropdown,
  Menu,
  Progress,
  Pagination,
  Drawer,
  Card,
  Button,
  Space,
  Tag,
} from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeFilled,
  DeleteFilled,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Item } from "../../../types";
import dayjs from "dayjs";
import WarningModal from "components/layout/components/WarningModal";

interface BatchTableProps {
  items: Item[];
  processingStatus: { [key: number]: string };
  progress: { [key: number]: number };
  processingItems: Item[];
  onItemClick: (itemId: string | any) => void;
  onAddClick: (id: any) => void;
  onDeleteSession: (item: Item) => void;
  total?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize?: number) => void;
  loading?: boolean;
}

export const BatchTableHistory: React.FC<BatchTableProps> = ({
  items,
  processingStatus,
  progress,
  processingItems,
  onItemClick,
  onAddClick,
  onDeleteSession,
  total = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  loading = false,
}) => {
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [item, setItem] = useState<Item | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [selectedMobileItem, setSelectedMobileItem] = useState<Item | null>(
    null,
  );

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getStatusDisplay = (item: Item) => {
    const isProcessingItem = processingItems.some(
      (processingItem) => processingItem.id === item.id,
    );

    if (isProcessingItem) {
      const currentStatus = processingStatus[item.id];
      const currentProgress = progress[item.id];

      if (item.status === "Processing" && currentStatus) {
        const progressInt =
          currentProgress !== undefined
            ? Math.round(currentProgress)
            : undefined;

        return (
          <div className="w-full">
            <div className="text-sm">{currentStatus}</div>
            {progressInt !== undefined && (
              <Progress
                percent={progressInt}
                size="small"
                status={progressInt === 100 ? "success" : "active"}
                strokeColor="#01065a"
              />
            )}
          </div>
        );
      }
    }

    const status = item.status || "Completed";
    const getStatusColor = () => {
      if (status === "Completed") return "success";
      if (status === "Processing") return "processing";
      if (status === "Failed") return "error";
      return "default";
    };

    return (
      <Tag color={getStatusColor()} className="text-xs">
        {status}
      </Tag>
    );
  };

  const handleMobileItemClick = (item: Item) => {
    setSelectedMobileItem(item);
    setMobileDrawerVisible(true);
  };

  const getProfileCount = (item: Item) => {
    return item.profiles_count ?? (item.files ?? item.profiles ?? []).length;
  };

  if (!isMobileView) {
    return (
      <div className="flex flex-col max-h-[70vh] h-full w-full bg-white rounded-lg shadow-sm">
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b font-semibold bg-gray-50">
                <th className="py-3 px-4 font-semibold text-gray-700">Name</th>
                <th className="py-3 px-4 font-semibold text-gray-700">
                  No of Profiles
                </th>
                <th className="py-3 px-4 font-semibold text-gray-700">Date</th>
                <th className="py-3 px-4 font-semibold text-gray-700">Time</th>
                <th className="py-3 px-4 font-semibold text-gray-700">
                  Last Updated
                </th>
                <th className="py-3 px-4 font-semibold text-gray-700">
                  Status
                </th>
                <th className="py-3 px-4 font-semibold text-gray-700">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    No data available
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                    onClick={() => onItemClick(item.id)}
                  >
                    <td className="py-2.5 px-4 font-medium text-gray-800">
                      {item.title}
                    </td>
                    <td className="py-2.5 px-4 text-gray-600">
                      {getProfileCount(item)}
                    </td>
                    <td className="py-2.5 px-4 text-gray-600">
                      {item.created_at
                        ? dayjs(item.created_at).format("DD/MM/YYYY")
                        : "--"}
                    </td>
                    <td className="py-2.5 px-4 text-gray-600">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "--"}
                    </td>
                    <td className="py-2.5 px-4 text-gray-600">
                      {item.updated_at
                        ? dayjs(item.updated_at).format("DD/MM/YYYY")
                        : "--"}{" "}
                      -{" "}
                      {item.updated_at
                        ? new Date(item.updated_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "--"}
                    </td>
                    <td className="py-2.5 px-4">{getStatusDisplay(item)}</td>
                    <td
                      className="py-2.5 px-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Dropdown
                        trigger={["click"]}
                        overlay={
                          <Menu>
                            <Menu.Item
                              key="view"
                              icon={<EyeFilled className="text-blue-500" />}
                              onClick={(e) => {
                                e.domEvent.stopPropagation();
                                onItemClick(item.id);
                              }}
                            >
                              <span className="text-blue-600 font-medium">
                                View
                              </span>
                            </Menu.Item>
                            <Menu.Item
                              key="add"
                              icon={<PlusOutlined className="text-blue-500" />}
                              onClick={(e) => {
                                e.domEvent.stopPropagation();
                                onAddClick(item);
                              }}
                            >
                              <span className="text-blue-600 font-medium">
                                Add
                              </span>
                            </Menu.Item>
                            <Menu.Item
                              key="delete"
                              icon={<DeleteFilled className="text-red-500" />}
                              onClick={() => {
                                setItem(item);
                                setDeleteModalVisible(true);
                              }}
                            >
                              <span className="text-red-600 font-medium">
                                Delete
                              </span>
                            </Menu.Item>
                          </Menu>
                        }
                      >
                        <MoreOutlined className="text-xl text-gray-600 cursor-pointer hover:text-blue-600 transition-colors" />
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="py-4 px-4 border-t bg-white rounded-b-lg flex-shrink-0">
            <div className="flex justify-end">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={onPageChange}
                showSizeChanger={true}
                showQuickJumper={true}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </div>
          </div>
        )}

        <WarningModal
          open={isDeleteModalVisible}
          title="Delete Batch"
          content={<>Are you sure you want to delete this batch?</>}
          onConfirm={() => {
            item && onDeleteSession(item);
            setDeleteModalVisible(false);
          }}
          onCancel={() => setDeleteModalVisible(false)}
          confirmText="Yes"
          cancelText="No"
        />
      </div>
    );
  }

  // Mobile Card View
  return (
    <>
      <div className="w-full">
        {/* Mobile Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Document Batches
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {total} batch{total !== 1 ? "es" : ""} total
          </p>
        </div>

        {/* Mobile Cards */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No data available
            </div>
          ) : (
            items.map((item) => (
              <Card
                key={item.id}
                className="cursor-pointer hover:shadow-md transition-shadow duration-200"
                onClick={() => handleMobileItemClick(item)}
                bodyStyle={{ padding: "12px" }}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FileTextOutlined className="text-gray-400" />
                      <span>{getProfileCount(item)} profiles</span>
                    </div>
                  </div>
                  <Dropdown
                    trigger={["click"]}
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="view"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.domEvent.stopPropagation();
                            onItemClick(item.id);
                          }}
                        >
                          View Details
                        </Menu.Item>
                        <Menu.Item
                          key="add"
                          icon={<PlusOutlined />}
                          onClick={(e) => {
                            e.domEvent.stopPropagation();
                            onAddClick(item);
                          }}
                        >
                          Add to Batch
                        </Menu.Item>
                        <Menu.Item
                          key="delete"
                          icon={<DeleteOutlined />}
                          danger
                          onClick={(e) => {
                            e.domEvent.stopPropagation();
                            setItem(item);
                            setDeleteModalVisible(true);
                          }}
                        >
                          Delete Batch
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    <Button
                      type="text"
                      icon={<MoreOutlined className="text-lg" />}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:bg-gray-100"
                    />
                  </Dropdown>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 text-gray-500">
                      <CalendarOutlined />
                      <span>Created:</span>
                    </div>
                    <span className="text-gray-700">
                      {item.created_at
                        ? dayjs(item.created_at).format("DD/MM/YYYY")
                        : "--"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 text-gray-500">
                      <ClockCircleOutlined />
                      <span>Time:</span>
                    </div>
                    <span className="text-gray-700">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "--"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 text-gray-500">
                      <CalendarOutlined />
                      <span>Last Updated:</span>
                    </div>
                    <span className="text-gray-700">
                      {item.updated_at
                        ? `${dayjs(item.updated_at).format("DD/MM/YYYY")} - ${new Date(
                            item.updated_at,
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}`
                        : "--"}
                    </span>
                  </div>

                  <div className="pt-2">{getStatusDisplay(item)}</div>
                </div>

                <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
                  <Button
                    size="small"
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemClick(item.id);
                    }}
                    className="flex-1"
                    style={{ backgroundColor: "#01065a" }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddClick(item);
                    }}
                    className="flex-1"
                  >
                    Add
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>

        {total > 0 && (
          <div className="mt-4 py-3">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              onChange={onPageChange}
              simple={true}
              size="small"
            />
            <div className="text-center text-xs text-gray-500 mt-2">
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, total)} of {total} items
            </div>
          </div>
        )}
      </div>

      {/* Mobile Drawer for Batch Details */}
      <Drawer
        title={selectedMobileItem?.title || "Batch Details"}
        placement="bottom"
        height="90%"
        open={mobileDrawerVisible}
        onClose={() => {
          setMobileDrawerVisible(false);
          setSelectedMobileItem(null);
        }}
        closable={true}
        destroyOnClose
        bodyStyle={{ padding: 0 }}
        headerStyle={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {selectedMobileItem && (
          <div className="p-4">
            <Card>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Batch Name</div>
                  <div className="font-medium">{selectedMobileItem.title}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Profiles</div>
                  <div>{getProfileCount(selectedMobileItem)} candidates</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Created Date</div>
                  <div>
                    {selectedMobileItem.created_at
                      ? dayjs(selectedMobileItem.created_at).format(
                          "DD/MM/YYYY HH:mm",
                        )
                      : "--"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Last Updated</div>
                  <div>
                    {selectedMobileItem.updated_at
                      ? dayjs(selectedMobileItem.updated_at).format(
                          "DD/MM/YYYY HH:mm",
                        )
                      : "--"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <div>{getStatusDisplay(selectedMobileItem)}</div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="primary"
                    block
                    onClick={() => {
                      setMobileDrawerVisible(false);
                      onItemClick(selectedMobileItem.id);
                    }}
                    style={{ backgroundColor: "#01065a" }}
                  >
                    View Details
                  </Button>
                  <Button
                    danger
                    block
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setMobileDrawerVisible(false);
                      setItem(selectedMobileItem);
                      setDeleteModalVisible(true);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Drawer>

      <WarningModal
        open={isDeleteModalVisible}
        title="Delete Batch"
        content={<>Are you sure you want to delete this batch?</>}
        onConfirm={() => {
          item && onDeleteSession(item);
          setDeleteModalVisible(false);
          setSelectedMobileItem(null);
        }}
        onCancel={() => setDeleteModalVisible(false)}
        confirmText="Yes"
        cancelText="No"
      />
    </>
  );
};
